import { PaginatedResponse, User, UserType } from '@/types';
import {
  SupportType,
  TicketMessage,
  TicketsResponse,
  TicketStatus,
} from '@/types/apps/ticket';

export const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://stage-api.chiptuning-x.ai/v1';

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (_e) {
        errorData = { message: response.statusText || 'An error occurred while processing the request' };
      }

      // Handle 500 errors globally
      if (response.status === 500) {
        // Dispatch custom event for global error handler (message will be translated in App.tsx)
        // eslint-disable-next-line no-undef
        window.dispatchEvent(new CustomEvent('api:error:500'));
        // Don't throw error to prevent app crash, return a rejected promise instead
        return Promise.reject(new Error('Internal server error'));
      }

      throw new Error(
        errorData.message || 'An error occurred while processing the request',
      );
    }

    // Check if response has content to parse
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    } else {
      // Return undefined for empty responses (like DELETE)
      return undefined as T;
    }
  }

  async getUsers(page = 1, limit = 10): Promise<PaginatedResponse<User>> {
    const response = await this.request<PaginatedResponse<User>>(
      `/users?page=${page}&limit=${limit}`,
    );
    return response;
  }

  async getUser(id: string): Promise<User> {
    const response = await this.request<User>(`/users/${id}`);
    return response;
  }

  async createUser(userData: Omit<User, 'id'>): Promise<User> {
    const response = await this.request<User>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return response;
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const response = await this.request<User>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return response;
  }

  async deleteUser(id: string): Promise<void> {
    await this.request<void>(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  async login(
    email: string,
    password: string,
  ): Promise<{ user: User; token: string }> {
    const response = await this.request<{ accessToken: string; user: any }>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      },
    );

    const { accessToken, user } = response;

    // Determine display name based on customer type
    const displayName = user.customer?.customerType?.name === 'Business' && user.customer?.companyName
      ? user.customer.companyName
      : `${user.firstName} ${user.lastName}`;

    return {
      token: accessToken,
      user: {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        isActive: user.isActive,
        userType: user.userType,
        roleId: user.roleId,
        language: user.language,
        customer: user.customer,
        name: displayName,
      },
    };
  }

  async register(userData: {
    companyName?: string;
    firstName?: string;
    lastName?: string;
    email: string;
    password: string;
    phoneNumber: string;
    city: string;
    address: string;
    postCode: string;
    countryCode: string;
    vatNumber?: string;
    evcNumber?: string;
    autotunerEmail?: string;
    magicmotorsportsEmail?: string;
    customerType: string;
  }): Promise<{ user: User; token: string }> {
    const response = await this.request<{ accessToken?: string; user: any }>(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify(userData),
      },
    );

    const { accessToken, user } = response;

    return {
      token: accessToken || `mock-token-${  Date.now()}`,
      user: {
        id: user.id || `user-${  Date.now()}`,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        isActive: user.isActive !== undefined ? user.isActive : true,
        userType: user.userType || 'customer',
        roleId: user.roleId,
        customer: user.customer,
        name: `${user.firstName} ${user.lastName}`,
      },
    };
  }


  async getCurrentUser(): Promise<User> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;
    const response = await this.request<User>('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }

  async requestPasswordRecovery(email: string): Promise<{ message: string }> {
    const response = await this.request<{ message: string }>(
      '/auth/request-password-recovery',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      },
    );
    return response;
  }

  async sendCustomerVerification(email: string): Promise<{ message: string }> {
    const response = await this.request<{ message: string }>(
      '/auth/send-customer-verification',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      },
    );
    return response;
  }

  async verifyPasswordRecovery(data: {
    email: string;
    code: string;
    newPassword: string;
  }): Promise<{ message: string }> {
    const response = await this.request<{ message: string }>(
      '/auth/verify-password-recovery',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
    return response;
  }

  async verifyCustomerEmail(data: {
    email: string;
    code: string;
  }): Promise<{ message: string }> {
    const response = await this.request<{ message: string }>(
      '/auth/verify-customer-email',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
    return response;
  }

  async getTickets(
    page: number = 1,
    limit: number = 10,
    status?: TicketStatus,
    ticketId?: string,
  ): Promise<TicketsResponse> {
    const tokenRaw = localStorage.getItem('auth_token');
    // Прибираємо лапки якщо вони є (через JSON.stringify при збереженні)
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;
    const searchParams = new URLSearchParams();
    searchParams.append('page', page.toString());
    searchParams.append('limit', limit.toString());
    if (status) {
      searchParams.append('status', status);
    }
    if (ticketId?.trim()) {
      searchParams.append('shortExternalId', ticketId.trim());
    }
    const endpoint = `/support/tickets?${searchParams.toString()}`;

    const response = await this.request<TicketsResponse>(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }

  async createTicket(data: {
    supportType: SupportType;
    subject: string;
    message: string;
    file?: File;
  }): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    console.log(
      'Creating ticket with token:',
      token ? 'Token exists' : 'No token',
    );

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const formData = new FormData();
    formData.append('supportType', data.supportType);
    formData.append('subject', data.subject);
    formData.append('message', data.message);

    if (data.file) {
      formData.append('file', data.file);
    }

    const url = `${API_BASE_URL}/support/tickets`;

    console.log('Request URL:', url);
    console.log('Request data:', {
      supportType: data.supportType,
      subject: data.subject,
      hasFile: !!data.file,
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = {
          message: `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      console.error('Error response:', errorData);

      if (response.status === 403) {
        throw new Error(
          'Access denied. You may not have permission to create tickets or your session has expired.',
        );
      }

      throw new Error(
        errorData.message || `Failed to create ticket (${response.status})`,
      );
    }

    return response.json();
  }

  async closeTicket(ticketId: string): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const response = await this.request<any>(
      `/support/tickets/${ticketId}/close`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response;
  }

  async getTicketMessages(ticketId: string): Promise<TicketMessage[]> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const response = await this.request<TicketMessage[]>(
      `/support/tickets/${ticketId}/messages`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response;
  }

  async sendTicketMessage(
    ticketId: string,
    data: {
      message: string;
      file?: File;
    },
  ): Promise<TicketMessage> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const formData = new FormData();
    formData.append('message', data.message);

    if (data.file) {
      formData.append('file', data.file);
    }

    const url = `${API_BASE_URL}/support/tickets/${ticketId}/messages`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = {
          message: `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      throw new Error(
        errorData.message || `Failed to send message (${response.status})`,
      );
    }

    return response.json();
  }

  async getCredentials(): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const url = `${API_BASE_URL}/credentials`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        // Credentials не існують - це нормально
        return {};
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'An error occurred while processing the request',
        );
      }

      return response.json();
    } catch (error: any) {
      // If this is not a fetch error, but our error - throw it further
      if (error.message && !error.message.includes('fetch')) {
        throw error;
      }
      // Other errors (network, etc.) throw further
      throw error;
    }
  }

  async updateCredentials(
    credentialsData: any,
    isFirstTime: boolean = false,
  ): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const method = isFirstTime ? 'POST' : 'PUT';

    return await this.request<any>('/credentials', {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentialsData),
    });
  }

  async getCompanyInfo(): Promise<{
    country: string;
    city: string;
    addresses: string;
    postCode: number;
    companyName: string;
  } | null> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const url = `${API_BASE_URL}/company-info`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        // Company info не існує - це нормально
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'An error occurred while processing the request',
        );
      }

      return response.json();
    } catch (error: any) {
      // If this is not a fetch error, but our error - throw it further
      if (error.message && !error.message.includes('fetch')) {
        throw error;
      }
      // Other errors (network, etc.) throw further
      throw error;
    }
  }

  async createCompanyInfo(data: {
    country: string;
    city: string;
    addresses: string;
    postCode: string;
    companyName: string;
  }): Promise<{
    country: string;
    city: string;
    addresses: string;
    postCode: number;
    companyName: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      country: string;
      city: string;
      addresses: string;
      postCode: number;
      companyName: string;
    }>('/company-info', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updateCompanyInfo(data: {
    country: string;
    city: string;
    addresses: string;
    postCode: string;
    companyName: string;
  }): Promise<{
    country: string;
    city: string;
    addresses: string;
    postCode: number;
    companyName: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      country: string;
      city: string;
      addresses: string;
      postCode: number;
      companyName: string;
    }>('/company-info', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async getNotice(): Promise<{
    id: string;
    messageEn?: string | null;
    messageDe?: string | null;
    messageRu?: string | null;
    messagePl?: string | null;
    type: string;
    isEnabled: boolean;
    expirationDate: string;
    createdAt: string;
    updatedAt: string;
  } | null> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const url = `${API_BASE_URL}/notice`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        // Notice не існує - це нормально
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'An error occurred while processing the request',
        );
      }

      return response.json();
    } catch (error: any) {
      // If this is not a fetch error, but our error - throw it further
      if (error.message && !error.message.includes('fetch')) {
        throw error;
      }
      // Other errors (network, etc.) throw further
      throw error;
    }
  }

  async createNotice(noticeData: {
    message: string;
    lang: string;
    type: string;
    isEnabled: boolean;
    expirationDate: string;
  }): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<any>('/notice', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(noticeData),
    });
  }

  async updateNotice(noticeData: {
    message: string;
    lang: string;
    type: string;
    isEnabled: boolean;
    expirationDate: string;
  }): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<any>('/notice', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(noticeData),
    });
  }

  async getSchedule(): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const url = `${API_BASE_URL}/schedule`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        // Schedule не існує - це нормально
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'An error occurred while processing the request',
        );
      }

      return response.json();
    } catch (error: any) {
      // If this is not a fetch error, but our error - throw it further
      if (error.message && !error.message.includes('fetch')) {
        throw error;
      }
      // Other errors (network, etc.) throw further
      throw error;
    }
  }

  async createSchedule(scheduleData: {
    sunday: { start: string; end: string; isClosed: boolean };
    monday: { start: string; end: string; isClosed: boolean };
    tuesday: { start: string; end: string; isClosed: boolean };
    wednesday: { start: string; end: string; isClosed: boolean };
    thursday: { start: string; end: string; isClosed: boolean };
    friday: { start: string; end: string; isClosed: boolean };
    saturday: { start: string; end: string; isClosed: boolean };
  }): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<any>('/schedule', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(scheduleData),
    });
  }

  async updateSchedule(scheduleData: {
    sunday: { start: string; end: string; isClosed: boolean };
    monday: { start: string; end: string; isClosed: boolean };
    tuesday: { start: string; end: string; isClosed: boolean };
    wednesday: { start: string; end: string; isClosed: boolean };
    thursday: { start: string; end: string; isClosed: boolean };
    friday: { start: string; end: string; isClosed: boolean };
    saturday: { start: string; end: string; isClosed: boolean };
  }): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<any>('/schedule', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(scheduleData),
    });
  }

  // Notification methods
  async getNotifications(): Promise<any> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<any>('/notifications', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<void>('/notifications/mark-read', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ notificationId }),
    });
  }

  // Customer Types methods
  async getCustomerTypes(requireAuth: boolean = true): Promise<{
    customerTypes: Array<{
      id: string;
      name: string;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const headers: Record<string, string> = {};
    
    if (requireAuth) {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
      }
      
      headers.Authorization = `Bearer ${token}`;
    }

    return await this.request<{
      customerTypes: Array<{
        id: string;
        name: string;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('/customers/types', {
      headers,
    });
  }

  // Customer Groups methods
  async getCustomerGroups(requireAuth: boolean = true): Promise<{
    groups: Array<{
      id: string;
      name: string;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const headers: Record<string, string> = {};

    if (requireAuth) {
      const tokenRaw = localStorage.getItem('auth_token');
      const token = tokenRaw ? JSON.parse(tokenRaw) : null;

      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      headers.Authorization = `Bearer ${token}`;
    }

    return await this.request<{
      groups: Array<{
        id: string;
        name: string;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('/customers/groups', {
      headers,
    });
  }

  async createCustomerGroup(data: { name: string }): Promise<{
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      name: string;
      createdAt: string;
      updatedAt: string;
    }>('/customers/groups', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updateCustomerGroup(
    id: string,
    data: { name: string },
  ): Promise<{
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      name: string;
      createdAt: string;
      updatedAt: string;
    }>(`/customers/groups/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async deleteCustomerGroup(id: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/customers/groups/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createCustomerType(data: { name: string }): Promise<{
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      name: string;
      createdAt: string;
      updatedAt: string;
    }>('/customers/types', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updateCustomerType(
    id: string,
    data: { name: string },
  ): Promise<{
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      name: string;
      createdAt: string;
      updatedAt: string;
    }>(`/customers/types/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async deleteCustomerType(id: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/customers/types/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Customers methods
  async getCustomers(
    page: number = 1,
    limit: number = 10,
    filters?: {
      email?: string;
      phoneNumber?: string;
      firstName?: string;
      lastName?: string;
      customerGroupId?: string;
    },
  ): Promise<{
    customers: Array<{
      id: string;
      companyName: string;
      email: string;
      customerType: {
        id: string;
        name: string;
      };
      phoneNumber: string;
      city: string;
      country: {
        id: string;
        name: string;
        isoCode: string;
      };
      postCode: string;
      address: string;
      vatNumber: string;
      evcNumber: string;
      autotunerEmail?: string | null;
      magicmotorsportsEmail?: string | null;
      slaveSerialNumber?: string | null;
      firstName: string;
      lastName: string;
      balance: number;
      isActive: boolean;
      isVerified: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const searchParams = new URLSearchParams();
    searchParams.append('page', page.toString());
    searchParams.append('limit', limit.toString());
    if (filters?.email?.trim()) {
      searchParams.append('email', filters.email.trim());
    }
    if (filters?.phoneNumber?.trim()) {
      searchParams.append('phoneNumber', filters.phoneNumber.trim());
    }
    if (filters?.firstName?.trim()) {
      searchParams.append('firstName', filters.firstName.trim());
    }
    if (filters?.lastName?.trim()) {
      searchParams.append('lastName', filters.lastName.trim());
    }
    if (filters?.customerGroupId?.trim()) {
      searchParams.append('customerGroupId', filters.customerGroupId.trim());
    }

    return await this.request<{
      customers: Array<{
        id: string;
        companyName: string;
        email: string;
        customerType: {
          id: string;
          name: string;
        };
        phoneNumber: string;
        city: string;
        country: {
          id: string;
          name: string;
          isoCode: string;
        };
        postCode: string;
        address: string;
        vatNumber: string;
        evcNumber: string;
        autotunerEmail?: string | null;
        magicmotorsportsEmail?: string | null;
        slaveSerialNumber?: string | null;
        firstName: string;
        lastName: string;
        balance: number;
        isActive: boolean;
        isVerified: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/customers?${searchParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async adjustCustomerBalance(
    customerId: string,
    data: {
      type: 'debit' | 'credit';
      amount: number;
      reason?: string;
    },
  ): Promise<{
    balance: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      balance: number;
    }>(`/customers/${customerId}/adjust-balance`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async createCustomer(data: {
    companyName: string;
    customerTypeId: string;
    phoneNumber: string;
    city: string;
    countryId: string;
    postCode: string;
    address: string;
    vatNumber: string;
    evcNumber: string;
    autotunerEmail?: string;
    magicmotorsportsEmail?: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    slaveSerialNumber?: string;
    customerGroupId?: string;
    isActive: boolean;
    isVerified: boolean;
  }): Promise<{
    id: string;
    companyName: string;
    customerType: {
      id: string;
      name: string;
    };
    phoneNumber: string;
    city: string;
    country: {
      id: string;
      name: string;
      isoCode: string;
    };
    postCode: string;
    address: string;
    vatNumber: string;
    evcNumber: string;
    firstName: string;
    lastName: string;
    email: string;
    balance: number;
    isActive: boolean;
    isVerified: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      companyName: string;
      firstName: string;
      lastName: string;
      email: string;
      balance: number;
      customerType: {
        id: string;
        name: string;
      };
      phoneNumber: string;
      city: string;
      country: {
        id: string;
        name: string;
        isoCode: string;
      };
      postCode: string;
      address: string;
      vatNumber: string;
      evcNumber: string;
      slaveSerialNumber?: string | null;
      isActive: boolean;
      isVerified: boolean;
      createdAt: string;
      updatedAt: string;
    }>('/customers', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async getCountries(): Promise<{
    items: Array<{
      id: string;
      name: string;
      isoCode: string;
      phoneCode: string;
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      items: Array<{
        id: string;
        name: string;
        isoCode: string;
        phoneCode: string;
      }>;
    }>('/countries', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getCustomerById(id: string): Promise<{
    id: string;
    companyName: string;
    customerType: {
      id: string;
      name: string;
    };
    customerGroup?: { id: string; name: string } | null;
    phoneNumber: string;
    city: string;
    country: {
      id: string;
      name: string;
      isoCode: string;
    };
    postCode: string;
    address: string;
    vatNumber: string;
    evcNumber: string;
    firstName: string;
    lastName: string;
    email: string;
    slaveSerialNumber?: string | null;
    balance: number;
    isActive: boolean;
    isVerified: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      companyName: string;
      customerType: {
        id: string;
        name: string;
      };
      customerGroup?: { id: string; name: string } | null;
      phoneNumber: string;
      city: string;
      country: {
        id: string;
        name: string;
        isoCode: string;
      };
      postCode: string;
      address: string;
      vatNumber: string;
      evcNumber: string;
      firstName: string;
      lastName: string;
      email: string;
      slaveSerialNumber?: string | null;
      balance: number;
      isActive: boolean;
      isVerified: boolean;
      createdAt: string;
      updatedAt: string;
    }>(`/customers/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateCustomer(
    id: string,
    data: {
      companyName: string;
      customerTypeId: string;
      phoneNumber: string;
      city: string;
      countryId: string;
      postCode: string;
      address: string;
      vatNumber: string;
      evcNumber: string;
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      customerGroupId?: string;
      slaveSerialNumber?: string;
      isActive: boolean;
      isVerified: boolean;
    },
  ): Promise<{
    id: string;
    companyName: string;
    customerType: {
      id: string;
      name: string;
    };
    phoneNumber: string;
    city: string;
    country: {
      id: string;
      name: string;
      isoCode: string;
    };
    postCode: string;
    address: string;
    vatNumber: string;
    evcNumber: string;
    firstName: string;
    lastName: string;
    email: string;
    slaveSerialNumber?: string | null;
    balance: number;
    isActive: boolean;
    isVerified: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      companyName: string;
      balance: number;
      customerType: {
        id: string;
        name: string;
      };
      phoneNumber: string;
      city: string;
      country: {
        id: string;
        name: string;
        isoCode: string;
      };
      postCode: string;
      address: string;
      vatNumber: string;
      evcNumber: string;
      slaveSerialNumber?: string | null;
      isActive: boolean;
      isVerified: boolean;
      createdAt: string;
      updatedAt: string;
    }>(`/customers/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async deleteCustomer(id: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/customers/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getCreditPackages(): Promise<{
    packages: Array<{
      externalId: string;
      name: string;
      description: string;
      price: number;
      discountPrice: number;
      credits: number;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      packages: Array<{
        externalId: string;
        name: string;
        description: string;
        price: number;
        discountPrice: number;
        credits: number;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('/credit-packages', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-store', // завжди свіжі дані при переході на /shop
    });
  }

  async createCreditPackage(data: {
    name: string;
    description: string;
    price: number;
    discountPrice: number;
    credits: number;
  }): Promise<{
    externalId: string;
    name: string;
    description: string;
    price: number;
    discountPrice: number;
    credits: number;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      name: string;
      description: string;
      price: number;
      discountPrice: number;
      credits: number;
      createdAt: string;
      updatedAt: string;
    }>('/credit-packages', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updateCreditPackage(
    externalId: string,
    data: {
      name: string;
      description: string;
      price: number;
      discountPrice: number;
      credits: number;
    },
  ): Promise<{
    externalId: string;
    name: string;
    description: string;
    price: number;
    discountPrice: number;
    credits: number;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      name: string;
      description: string;
      price: number;
      discountPrice: number;
      credits: number;
      createdAt: string;
      updatedAt: string;
    }>(`/credit-packages/${externalId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async deleteCreditPackage(externalId: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/credit-packages/${externalId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getCreditPackageGroupPrices(externalId: string): Promise<
    Array<{
      groupId: string;
      groupName: string;
      price: number;
      discountPrice: number;
    }>
  > {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<
      Array<{
        groupId: string;
        groupName: string;
        price: number;
        discountPrice: number;
      }>
    >(`/credit-packages/${externalId}/group-prices`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateCreditPackageGroupPrice(
    externalId: string,
    groupId: string,
    data: { price: number; discountPrice: number },
  ): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(
      `/credit-packages/${externalId}/group-prices/${groupId}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      },
    );
  }

  async getRoles(
    page: number = 1,
    limit: number = 10,
  ): Promise<{
    roles: Array<{
      externalId: string;
      name: string;
      description: string;
      createdAt: string;
      updatedAt: string;
      permissions: Array<{
        externalId: string;
        action: string;
        resource: {
          externalId: string;
          name: string;
        };
      }>;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      roles: Array<{
        externalId: string;
        name: string;
        description: string;
        createdAt: string;
        updatedAt: string;
        permissions: Array<{
          externalId: string;
          action: string;
          resource: {
            externalId: string;
            name: string;
          };
        }>;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/acl/roles?page=${page}&limit=${limit}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createRole(data: {
    name: string;
    description: string;
    permissionExternalIds: string[];
  }): Promise<{
    externalId: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
    permissions: Array<{
      externalId: string;
      action: string;
      resource: {
        externalId: string;
        name: string;
      };
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      name: string;
      description: string;
      createdAt: string;
      updatedAt: string;
      permissions: Array<{
        externalId: string;
        action: string;
        resource: {
          externalId: string;
          name: string;
        };
      }>;
    }>('/acl/roles', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updateRole(
    externalId: string,
    data: {
      name: string;
      description: string;
      permissionExternalIds: string[];
    },
  ): Promise<{
    externalId: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
    permissions: Array<{
      externalId: string;
      action: string;
      resource: {
        externalId: string;
        name: string;
      };
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      name: string;
      description: string;
      createdAt: string;
      updatedAt: string;
      permissions: Array<{
        externalId: string;
        action: string;
        resource: {
          externalId: string;
          name: string;
        };
      }>;
    }>(`/acl/roles/${externalId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async deleteRole(externalId: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/acl/roles/${externalId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getResources(): Promise<{
    resources: Array<{
      externalId: string;
      name: string;
      roles: Array<{
        externalId: string;
        action: string;
      }>;
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      resources: Array<{
        externalId: string;
        name: string;
        roles: Array<{
          externalId: string;
          action: string;
        }>;
      }>;
    }>('/acl/resources', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getOBDCodes(
    page: number = 1,
    limit: number = 10,
    q?: string,
  ): Promise<{
    items: Array<{
      id: string;
      code: string;
      description: string;
    }>;
    total: number;
    page: number;
    limit: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    let endpoint = `/obd-codes?page=${page}&limit=${limit}`;
    if (q) {
      endpoint += `&q=${encodeURIComponent(q)}`;
    }

    return await this.request<{
      items: Array<{
        id: string;
        code: string;
        description: string;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getBoschCodes(params?: {
    page?: number;
    limit?: number;
    q?: string;
  }): Promise<{
    items: Array<{
      id: string;
      code: string;
      modelId: string;
      modelName: string;
      brandId: string;
      brandName: string;
    }>;
    total: number;
    page: number;
    limit: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const searchParams = new URLSearchParams();
    searchParams.append('page', (params?.page ?? 1).toString());
    searchParams.append('limit', (params?.limit ?? 10).toString());
    if (params?.q) {
      searchParams.append('q', params.q);
    }

    return await this.request<{
      items: Array<{
        id: string;
        code: string;
        modelId: string;
        modelName: string;
        brandId: string;
        brandName: string;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/ecu?${searchParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getTransactions(params?: { page?: number; limit?: number }): Promise<{
    transactions: Array<{
      id: string;
      type: string;
      productId?: string | null;
      productType?: string | null;
      productDescription?: string | null;
      productName?: string | null;
      customerCompanyName?: string | null;
      customerFirstName?: string | null;
      customerLastName?: string | null;
      creditAmount: number;
      previousBalanceAmount: number;
      createdAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const searchParams = new URLSearchParams();
    searchParams.append('page', (params?.page ?? 1).toString());
    searchParams.append('limit', (params?.limit ?? 10).toString());

    return await this.request<{
      transactions: Array<{
        id: string;
        type: string;
        productId?: string | null;
        productType?: string | null;
        productDescription?: string | null;
        productName?: string | null;
        customerCompanyName?: string | null;
        customerFirstName?: string | null;
        customerLastName?: string | null;
        creditAmount: number;
        previousBalanceAmount: number;
        createdAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/transactions?${searchParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getFilesQueue(): Promise<{
    processingCount: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{ processingCount: number }>('/files/queue', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getFiles(params?: {
    page?: number;
    limit?: number;
    status?: 'PROCESSING' | 'COMPLETED' | 'CLOSED' | 'DECLINED';
    ecu?: string;
    carBrandId?: string;
    carModel?: string;
    vinCode?: string;
    shortExternalId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{
    files: Array<{
      id: string;
      shortId?: string | null;
      status: 'PROCESSING' | 'COMPLETED' | 'CLOSED' | 'DECLINED';
      vehicleType?: string | null;
      carBrand?: string | null;
      carModel?: string | null;
      carEngine?: string | null;
      carPower?: string | null;
      carYear?: string | null;
      carGearbox?: string | null;
      carFuelType?: string | null;
      ecu?: string | null;
      vinCode?: string | null;
      readingTool?: string | null;
      customReadingTool?: string | null;
      readingMode?: string | null;
      hasNewMessage?: boolean | null;
      systemDescription?: string | null;
      creditsAmount?: number | null;
      originalFileUrl?: string | null;
      modifiedFileUrl?: string | null;
      decodedOriginalFileUrl?: string | null;
      memoryType?: string | null;
      isDecrypted?: boolean | null;
      createdAt: string;
      updatedAt: string;
      solutions?: string[];
      autotunerEmail?: string | null;
      magicmotorsportsEmail?: string | null;
      customer?: {
        companyName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
        creditsAmount?: number | null;
        customerTypeId?: string | null;
        customerTypeName?: string | null;
        slaveSerialNumber?: string | null;
      } | null;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const searchParams = new URLSearchParams();
    searchParams.append('page', (params?.page ?? 1).toString());
    searchParams.append('limit', (params?.limit ?? 10).toString());
    if (params?.status) {
      searchParams.append('status', params.status);
    }
    if (params?.ecu?.trim()) {
      searchParams.append('ecu', params.ecu.trim());
    }
    if (params?.carBrandId?.trim()) {
      searchParams.append('carBrandId', params.carBrandId.trim());
    }
    if (params?.carModel?.trim()) {
      searchParams.append('carModel', params.carModel.trim());
    }
    if (params?.vinCode?.trim()) {
      searchParams.append('vinCode', params.vinCode.trim());
    }
    if (params?.shortExternalId?.trim()) {
      searchParams.append('shortExternalId', params.shortExternalId.trim());
    }
    if (params?.dateFrom) {
      searchParams.append('dateFrom', params.dateFrom);
    }
    if (params?.dateTo) {
      searchParams.append('dateTo', params.dateTo);
    }

    return await this.request<{
      files: Array<{
        id: string;
        shortId?: string | null;
        status: 'PROCESSING' | 'COMPLETED' | 'CLOSED' | 'DECLINED';
        vehicleType?: string | null;
        carBrand?: string | null;
        carModel?: string | null;
        carEngine?: string | null;
        carPower?: string | null;
        carYear?: string | null;
        carGearbox?: string | null;
        carFuelType?: string | null;
        ecu?: string | null;
        vinCode?: string | null;
        readingTool?: string | null;
        customReadingTool?: string | null;
        readingMode?: string | null;
        hasNewMessage?: boolean | null;
        systemDescription?: string | null;
        creditsAmount?: number | null;
        originalFileUrl?: string | null;
        modifiedFileUrl?: string | null;
        decodedOriginalFileUrl?: string | null;
        memoryType?: string | null;
        isDecrypted?: boolean | null;
        createdAt: string;
        updatedAt: string;
        solutions?: string[];
        autotunerEmail?: string | null;
        magicmotorsportsEmail?: string | null;
        customer?: {
          companyName?: string | null;
          firstName?: string | null;
          lastName?: string | null;
          email?: string | null;
          creditsAmount?: number | null;
          customerTypeId?: string | null;
          customerTypeName?: string | null;
          slaveSerialNumber?: string | null;
        } | null;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/files?${searchParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getFile(fileId: string): Promise<{
    id: string;
    shortId?: string | null;
    status: 'PROCESSING' | 'COMPLETED' | 'CLOSED' | 'CANCELLED';
    vehicleType: string;
    carBrand: string;
    carModel: string;
    carEngine: string;
    carPower: string;
    carYear: string;
    carGearbox: string;
    carFuelType: string;
    ecu: string;
    vinCode?: string | null;
    extendedFileName?: string | null;
    readingTool: string;
    customReadingTool?: string | null;
    readingMode?: string | null;
    hasNewMessage?: boolean | null;
    systemDescription?: string | null;
    creditsAmount?: number | null;
    originalFileUrl?: string | null;
    modifiedFileUrl?: string | null;
    decodedOriginalFileUrl?: string | null;
    memoryType?: string | null;
    isDecrypted?: boolean | null;
    createdAt: string;
    updatedAt: string;
    solutions: string[];
    autotunerEmail?: string | null;
    magicmotorsportsEmail?: string | null;
    customer?: {
      companyName?: string | null;
      firstName: string;
      lastName: string;
      email: string;
      creditsAmount?: number | null;
      customerTypeId?: string | null;
      customerTypeName?: string | null;
      slaveSerialNumber?: string | null;
    } | null;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      shortId?: string | null;
      status: 'PROCESSING' | 'COMPLETED' | 'CLOSED' | 'CANCELLED';
      vehicleType: string;
      carBrand: string;
      carModel: string;
      carEngine: string;
      carPower: string;
      carYear: string;
      carGearbox: string;
      carFuelType: string;
      ecu: string;
      vinCode?: string | null;
      extendedFileName?: string | null;
      readingTool: string;
      customReadingTool?: string | null;
      readingMode?: string | null;
      hasNewMessage?: boolean | null;
      systemDescription?: string | null;
      creditsAmount?: number | null;
      originalFileUrl?: string | null;
      modifiedFileUrl?: string | null;
      decodedOriginalFileUrl?: string | null;
      memoryType?: string | null;
      isDecrypted?: boolean | null;
      createdAt: string;
      updatedAt: string;
      solutions: string[];
      autotunerEmail?: string | null;
      magicmotorsportsEmail?: string | null;
      customer?: {
        companyName?: string | null;
        firstName: string;
        lastName: string;
        email: string;
        creditsAmount?: number | null;
        customerTypeId?: string | null;
        customerTypeName?: string | null;
        slaveSerialNumber?: string | null;
      } | null;
    }>(`/files/${fileId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getFileMessages(fileId: string): Promise<
    Array<{
      id: string;
      message?: string | null;
      fileUrl?: string | null;
      fileOriginalName?: string | null;
      sender: string;
      createdAt: string;
    }>
  > {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<
      Array<{
        id: string;
        message?: string | null;
        fileUrl?: string | null;
        fileOriginalName?: string | null;
        sender: string;
        createdAt: string;
      }>
    >(`/files/${fileId}/messages`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async sendFileMessage(
    fileId: string,
    data: { message?: string; file?: File },
  ): Promise<{
    id: string;
    message?: string | null;
    fileUrl?: string | null;
    fileOriginalName?: string | null;
    sender: string;
    createdAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const formData = new FormData();
    if (data.message) {
      formData.append('message', data.message);
    }
    if (data.file) {
      formData.append('file', data.file);
    }

    const response = await fetch(`${API_BASE_URL}/files/${fileId}/messages`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to send message (status ${response.status})`,
      );
    }

    return response.json();
  }

  async updateFileStatus(
    fileId: string,
    data: {
      status: 'PROCESSING' | 'COMPLETED' | 'CLOSED' | 'CANCELLED';
      file?: File;
      encodeType?: string;
    },
  ): Promise<{
    id: string;
    status: 'PROCESSING' | 'COMPLETED' | 'CLOSED';
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const formData = new FormData();
    formData.append('status', data.status);
    if (data.file) {
      formData.append('modified_file', data.file);
    }
    if (data.encodeType) {
      formData.append('encryptMemoryType', data.encodeType);
    }

    const response = await fetch(`${API_BASE_URL}/files/${fileId}/status`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to update status (status ${response.status})`,
      );
    }

    return response.json();
  }


  async createFileService(data: {
    vehicleTypeId: string;
    carBrandId: string;
    carModel: string;
    carEngine: string;
    carPower: string;
    carYear: string;
    carGearbox: string;
    carFuelType: string;
    ecu: string;
    vinCode?: string;
    readingMode: string;
    customReadingTool?: string;
    solutions: string[];
    // eslint-disable-next-line no-undef
    originalFile: File;
  }): Promise<{ id: string }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const formData = new FormData();
    formData.append('vehicleTypeId', data.vehicleTypeId);
    formData.append('carBrandId', data.carBrandId);
    formData.append('carModel', data.carModel);
    formData.append('carEngine', data.carEngine);
    formData.append('carPower', data.carPower);
    formData.append('carYear', data.carYear);
    formData.append('carGearbox', data.carGearbox);
    formData.append('carFuelType', data.carFuelType);
    formData.append('ecu', data.ecu);
    if (data.vinCode) {
      formData.append('vinCode', data.vinCode);
    }
    formData.append('readingMode', data.readingMode);
    if (data.customReadingTool) {
      formData.append('customReadingTool', data.customReadingTool);
    }
    formData.append('solutions', JSON.stringify(data.solutions));
    formData.append('original_file', data.originalFile);

    const response = await fetch(`${API_BASE_URL}/files`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (error) {
        errorData = { message: response.statusText };
      }
      throw new Error(errorData.message || 'Failed to create file service.');
    }

    return response.json();
  }

  async getEmployees(
    page: number = 1,
    limit: number = 10,
  ): Promise<{
    employees: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      roleId: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      employees: Array<{
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        roleId: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/employees?page=${page}&limit=${limit}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createEmployee(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    roleExternalId: string;
    isActive: boolean;
  }): Promise<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    roleExternalId: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      roleExternalId: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>('/employees', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updateEmployee(
    id: string,
    data: {
      firstName: string;
      lastName: string;
      phoneNumber: string;
      email: string;
      roleExternalId: string;
      isActive: boolean;
    },
  ): Promise<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    roleExternalId: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      roleExternalId: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>(`/employees/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async deleteEmployee(id: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<void>(`/employees/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getCarBrands(search?: string, getAll?: boolean): Promise<{
    items: Array<{
      id: string;
      name: string;
      logo: string;
      image: string;
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    let endpoint = '/cars/brands';
    const queryParams: string[] = [];
    if (search) {
      queryParams.push(`search=${encodeURIComponent(search)}`);
    }
    if (getAll) {
      queryParams.push('get_all=true');
    }
    if (queryParams.length > 0) {
      endpoint += `?${queryParams.join('&')}`;
    }

    return await this.request<{
      items: Array<{
        id: string;
        name: string;
        logo: string;
        image: string;
      }>;
    }>(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getCarModels(params: {
    brand_id: string;
    search?: string;
    model_id?: string;
    year?: string;
    engine_id?: string;
  }): Promise<{
    items: Array<{
      id: string;
      name: string;
      year: string;
      years: string[];
      engine: {
        id: string;
        name: string;
        gearbox: string;
        fuel: string;
      };
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const queryParams = new URLSearchParams();
    queryParams.append('brand_id', params.brand_id);
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.model_id) {
      queryParams.append('model_id', params.model_id);
    }
    if (params.year) {
      queryParams.append('year', params.year);
    }
    if (params.engine_id) {
      queryParams.append('engine_id', params.engine_id);
    }

    return await this.request<{
      items: Array<{
        id: string;
        name: string;
        year: string;
        years: string[];
        engine: {
          id: string;
          name: string;
          gearbox: string;
          fuel: string;
        };
      }>;
    }>(`/cars/models?${queryParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getCarByModel(modelId: string): Promise<{
    id: string;
    name: string;
    year: string;
    brand: {
      id: string;
      name: string;
      logo: string;
      image: string;
    };
    vehicleType: {
      id: string;
      name: string;
    };
    engine: {
      id: string;
      name: string;
      cylinders: string;
      displacement: string;
      power: string;
      torque: string;
      fuelSystem: string;
      fuel: string;
      fuelCapacity: string;
      fuelCapacityOptional: string;
      topSpeed: string;
      topSpeedElectrical: string;
      acceleration0_62Mph: string;
      driveType: string;
      gearbox: string;
      frontBrakes: string;
      rearBrakes: string;
      tireSize: string;
      length: string;
      width: string;
      height: string;
      frontRearTrack: string;
      wheelbase: string;
      groundClearance: string;
      cargoVolume: string;
      aerodynamicsFrontalArea: string;
      aerodynamicsCd: string;
      turningCircle: string;
      turningCircleWallToWall: string;
      turningCircleCurbToCurb: string;
      unladenWeight: string;
      grossWeightLimit: string;
      cityMpg: string;
      highwayMpg: string;
      combinedMpg: string;
      lowMpg: string;
      mediumMpg: string;
      highMpg: string;
      extraHighMpg: string;
      cityCng: string;
      highwayCng: string;
      combinedCng: string;
      lowCng: string;
      mediumCng: string;
      highCng: string;
      extraHighCng: string;
      fuelCapacityCng: string;
      cngCylinderCapacity: string;
      co2Emissions: string;
      co2EmissionsCombined: string;
      co2EmissionsLow: string;
      co2EmissionsMedium: string;
      co2EmissionsHigh: string;
      co2EmissionsExtraHigh: string;
      totalMaximumPower: string;
      totalMaximumTorque: string;
      powerPack: string;
      nominalCapacity: string;
      maximumCapacity: string;
      rangeKm: string;
      electricalMotorPower: string;
      electricalMotorTorque: string;
      chargerType: string;
      chargingTimeNormal: string;
      chargingTimeQuick: string;
    };
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      name: string;
      year: string;
      brand: {
        id: string;
        name: string;
        logo: string;
        image: string;
      };
      vehicleType: {
        id: string;
        name: string;
      };
      engine: {
        id: string;
        name: string;
        cylinders: string;
        displacement: string;
        power: string;
        torque: string;
        fuelSystem: string;
        fuel: string;
        fuelCapacity: string;
        fuelCapacityOptional: string;
        topSpeed: string;
        topSpeedElectrical: string;
        acceleration0_62Mph: string;
        driveType: string;
        gearbox: string;
        frontBrakes: string;
        rearBrakes: string;
        tireSize: string;
        length: string;
        width: string;
        height: string;
        frontRearTrack: string;
        wheelbase: string;
        groundClearance: string;
        cargoVolume: string;
        aerodynamicsFrontalArea: string;
        aerodynamicsCd: string;
        turningCircle: string;
        turningCircleWallToWall: string;
        turningCircleCurbToCurb: string;
        unladenWeight: string;
        grossWeightLimit: string;
        cityMpg: string;
        highwayMpg: string;
        combinedMpg: string;
        lowMpg: string;
        mediumMpg: string;
        highMpg: string;
        extraHighMpg: string;
        cityCng: string;
        highwayCng: string;
        combinedCng: string;
        lowCng: string;
        mediumCng: string;
        highCng: string;
        extraHighCng: string;
        fuelCapacityCng: string;
        cngCylinderCapacity: string;
        co2Emissions: string;
        co2EmissionsCombined: string;
        co2EmissionsLow: string;
        co2EmissionsMedium: string;
        co2EmissionsHigh: string;
        co2EmissionsExtraHigh: string;
        totalMaximumPower: string;
        totalMaximumTorque: string;
        powerPack: string;
        nominalCapacity: string;
        maximumCapacity: string;
        rangeKm: string;
        electricalMotorPower: string;
        electricalMotorTorque: string;
        chargerType: string;
        chargingTimeNormal: string;
        chargingTimeQuick: string;
      };
    }>(`/cars/by-model/${modelId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getEcuBrands(search?: string): Promise<{
    items: Array<{
      id: string;
      name: string;
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const searchParams = new URLSearchParams();
    if (search) {
      searchParams.append('search', search);
    }

    const endpoint = `/ecu/brands${
      searchParams.toString() ? `?${searchParams.toString()}` : ''
    }`;

    return await this.request<{
      items: Array<{
        id: string;
        name: string;
      }>;
    }>(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getEcuModels(params: { brand_id: string; search?: string }): Promise<{
    items: Array<{
      id: string;
      name: string;
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const searchParams = new URLSearchParams();
    searchParams.append('brand_id', params.brand_id);
    if (params.search) {
      searchParams.append('search', params.search);
    }

    return await this.request<{
      items: Array<{
        id: string;
        name: string;
      }>;
    }>(`/ecu/models?${searchParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getEcuDevices(params: {
    brand_id: string;
    model_id: string;
    search?: string;
  }): Promise<{
    items: Array<{
      id: string;
      name: string;
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const searchParams = new URLSearchParams();
    searchParams.append('brand_id', params.brand_id);
    searchParams.append('model_id', params.model_id);
    if (params.search) {
      searchParams.append('search', params.search);
    }

    return await this.request<{
      items: Array<{
        id: string;
        name: string;
      }>;
    }>(`/ecu/list?${searchParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getOrders(params?: { page?: number; limit?: number }): Promise<{
    orders: Array<{
      externalId: string;
      status: string;
      amount: number;
      currency: string;
      paymentMethod: string;
      customer?: {
        externalId: string;
        companyName?: string | null;
        city?: string | null;
        address?: string | null;
        postCode?: string | null;
        country?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      };
      creditPackage?: {
        externalId: string;
        name: string;
        credits: number;
        price: number;
      };
      seller?: {
        companyName?: string | null;
        address?: string | null;
        city?: string | null;
        postCode?: string | null;
        country?: string | null;
      };
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const searchParams = new URLSearchParams();
    if (params?.page) {
      searchParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }

    const endpoint = `/orders${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return await this.request<{
      orders: Array<{
        externalId: string;
        status: string;
        amount: number;
        currency: string;
        paymentMethod: string;
        customer?: {
          externalId: string;
          companyName?: string | null;
          city?: string | null;
          address?: string | null;
          postCode?: string | null;
          country?: string | null;
          firstName?: string | null;
          lastName?: string | null;
          email?: string | null;
        };
        creditPackage?: {
          externalId: string;
          name: string;
          credits: number;
          price: number;
        };
        seller?: {
          companyName?: string | null;
          address?: string | null;
          city?: string | null;
          postCode?: string | null;
          country?: string | null;
        };
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createManualOrder(data: {
    customerId: string;
    credits: number;
    amount: number;
    currency: string;
    description: string;
  }): Promise<{
    externalId: string;
    status: string;
    amount: number;
    currency: string;
    paymentMethod: string;
    customer?: {
      externalId: string;
      companyName?: string | null;
      city?: string | null;
      address?: string | null;
      postCode?: string | null;
      country?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    };
    creditPackage?: {
      externalId: string;
      name: string;
      credits: number;
      price: number;
    };
    seller?: {
      companyName?: string | null;
      address?: string | null;
      city?: string | null;
      postCode?: string | null;
      country?: string | null;
    };
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      status: string;
      amount: number;
      currency: string;
      paymentMethod: string;
      customer?: {
        externalId: string;
        companyName?: string | null;
        city?: string | null;
        address?: string | null;
        postCode?: string | null;
        country?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      };
      creditPackage?: {
        externalId: string;
        name: string;
        credits: number;
        price: number;
      };
      seller?: {
        companyName?: string | null;
        address?: string | null;
        city?: string | null;
        postCode?: string | null;
        country?: string | null;
      };
      createdAt: string;
      updatedAt: string;
    }>('/orders/manual', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async getOrdersExport(params: { from: string; to: string }): Promise<Blob> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const searchParams = new URLSearchParams();
    searchParams.append('from', params.from);
    searchParams.append('to', params.to);

    const url = `${API_BASE_URL}/orders/export?${searchParams.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      let errorData: { message?: string };
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          message: response.statusText || 'An error occurred while processing the request',
        };
      }
      if (response.status === 500) {
        window.dispatchEvent(new CustomEvent('api:error:500'));
        return Promise.reject(new Error('Internal server error'));
      }
      throw new Error(
        errorData.message || 'An error occurred while processing the request',
      );
    }

    return response.blob();
  }

  async getBalance(): Promise<{ balance: number }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{ balance: number }>('/balance', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getVehicleTypes(): Promise<{
    items: Array<{
      id: string;
      name: string;
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      items: Array<{
        id: string;
        name: string;
      }>;
    }>('/cars/vehicle-types', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getProfile(): Promise<User> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const response = await this.request<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      isActive: boolean;
      userType: string;
      roleId: string;
      language: string;
      customer?: {
        id: string;
        companyName: string;
        vatNumber?: string;
        evcNumber?: string;
        autotunerEmail?: string;
        magicmotorsportsEmail?: string;
        city?: string;
        address?: string;
        postCode?: string;
        countryId?: string;
        customerType?: {
          externalId: string;
          name: string;
        };
      };
    }>('/auth/profile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Determine display name based on customer type
    const displayName = response.customer?.customerType?.name === 'Business' && response.customer?.companyName
      ? response.customer.companyName
      : `${response.firstName} ${response.lastName}`;

    return {
      id: response.id,
      email: response.email,
      firstName: response.firstName,
      lastName: response.lastName,
      phoneNumber: response.phoneNumber,
      isActive: response.isActive,
      userType: response.userType as UserType,
      roleId: response.roleId,
      language: response.language,
      customer: response.customer,
      name: displayName,
    };
  }

  async updateProfile(data: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    companyName: string;
    vatNumber: string;
    evcNumber: string;
    city: string;
    address: string;
    postCode: string;
    countryId: string;
    autotunerEmail?: string;
    magicmotorsportsEmail?: string;
  }): Promise<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    isActive: boolean;
    userType: string;
    roleId: number;
    customer?: {
      id: string;
      companyName: string;
      vatNumber: string;
      evcNumber: string;
      city: string;
      address: string;
      postCode: string;
      countryId: string;
      autotunerEmail?: string;
      magicmotorsportsEmail?: string;
    };
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      isActive: boolean;
      userType: string;
      roleId: number;
      customer?: {
        id: string;
        companyName: string;
        vatNumber: string;
        evcNumber: string;
        city: string;
        address: string;
        postCode: string;
        countryId: string;
        autotunerEmail?: string;
        magicmotorsportsEmail?: string;
      };
    }>('/auth/profile', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async changePassword(data: {
    oldPassword: string;
    newPassword: string;
  }): Promise<{ message: string }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{ message: string }>('/auth/change-password', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async getSolutionCategories(): Promise<{
    items: Array<{
      externalId: string;
      name: string;
    }>;
    total: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      items: Array<{
        externalId: string;
        name: string;
      }>;
      total: number;
    }>('/solutions/categories', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async deleteSolutionCategory(externalId: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/solutions/categories/${externalId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateSolutionCategory(
    externalId: string,
    data: {
      name: string;
    },
  ): Promise<{
    externalId: string;
    name: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      name: string;
    }>(`/solutions/categories/${externalId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async createSolutionCategory(data: { name: string }): Promise<{
    externalId: string;
    name: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      name: string;
    }>('/solutions/categories', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async getSolutionSubcategories(): Promise<
    Array<{
      externalId: string;
      name: string;
      individualPrice: number;
      packagePrice: number;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<
      Array<{
        externalId: string;
        name: string;
        individualPrice: number;
        packagePrice: number;
        displayOrder: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      }>
    >('/solutions/subcategories', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createSolutionSubcategory(data: {
    name: string;
    individualPrice: number;
    packagePrice: number;
    displayOrder: number;
  }): Promise<{
    externalId: string;
    name: string;
    individualPrice: number;
    packagePrice: number;
    displayOrder: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      name: string;
      individualPrice: number;
      packagePrice: number;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>('/solutions/subcategories', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updateSolutionSubcategory(
    externalId: string,
    data: {
      name: string;
      individualPrice: number;
      packagePrice: number;
      displayOrder: number;
      isActive: boolean;
    },
  ): Promise<{
    externalId: string;
    name: string;
    individualPrice: number;
    packagePrice: number;
    displayOrder: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      name: string;
      individualPrice: number;
      packagePrice: number;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>(`/solutions/subcategories/${externalId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async deleteSolutionSubcategory(externalId: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/solutions/subcategories/${externalId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getPrices(): Promise<{
    items: Array<{
      externalId: string;
      priceCategoryId: string;
      solutionId: string;
      price: number;
      solution: {
        externalId: string;
        name: string;
        alias: string;
      };
      priceCategory?: {
        externalId: string;
        solutionCategoryId: string;
        priceMin: number;
        priceMax: number;
        solutionCategory: {
          externalId: string;
          name: string;
        };
        createdAt: string;
        updatedAt: string;
      };
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      items: Array<{
        externalId: string;
        priceCategoryId: string;
        solutionId: string;
        price: number;
        solution: {
          externalId: string;
          name: string;
          alias: string;
        };
        priceCategory?: {
          externalId: string;
          solutionCategoryId: string;
          priceMin: number;
          priceMax: number;
          solutionCategory: {
            externalId: string;
            name: string;
          };
          createdAt: string;
          updatedAt: string;
        };
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('/prices', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async deletePrice(externalId: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/prices/${externalId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getPriceCategories(): Promise<{
    items: Array<{
      externalId: string;
      solutionCategoryId: string;
      priceMin: number;
      priceMax: number;
      solutionCategory: {
        externalId: string;
        name: string;
      };
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      items: Array<{
        externalId: string;
        solutionCategoryId: string;
        priceMin: number;
        priceMax: number;
        solutionCategory: {
          externalId: string;
          name: string;
        };
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('/prices/categories', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async deletePriceCategory(externalId: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/prices/categories/${externalId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async createPriceCategory(data: {
    solutionCategoryId: string;
    priceMin: number;
    priceMax: number;
  }): Promise<{
    externalId: string;
    solutionCategoryId: string;
    priceMin: number;
    priceMax: number;
    solutionCategory: {
      externalId: string;
      name: string;
    };
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      solutionCategoryId: string;
      priceMin: number;
      priceMax: number;
      solutionCategory: {
        externalId: string;
        name: string;
      };
      createdAt: string;
      updatedAt: string;
    }>('/prices/categories', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updatePriceCategory(
    externalId: string,
    data: {
      solutionCategoryId: string;
      priceMin: number;
      priceMax: number;
    },
  ): Promise<{
    externalId: string;
    solutionCategoryId: string;
    priceMin: number;
    priceMax: number;
    solutionCategory: {
      externalId: string;
      name: string;
    };
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      solutionCategoryId: string;
      priceMin: number;
      priceMax: number;
      solutionCategory: {
        externalId: string;
        name: string;
      };
      createdAt: string;
      updatedAt: string;
    }>(`/prices/categories/${externalId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async createPrice(data: {
    priceCategoryId: string;
    solutionId: string;
    price: number;
  }): Promise<{
    externalId: string;
    priceCategoryId: string;
    solutionId: string;
    price: number;
    solution: {
      externalId: string;
      name: string;
      alias: string;
    };
    priceCategory?: {
      externalId: string;
      solutionCategoryId: string;
      priceMin: number;
      priceMax: number;
      solutionCategory: {
        externalId: string;
        name: string;
      };
      createdAt: string;
      updatedAt: string;
    };
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      priceCategoryId: string;
      solutionId: string;
      price: number;
      solution: {
        externalId: string;
        name: string;
        alias: string;
      };
      priceCategory?: {
        externalId: string;
        solutionCategoryId: string;
        priceMin: number;
        priceMax: number;
        solutionCategory: {
          externalId: string;
          name: string;
        };
        createdAt: string;
        updatedAt: string;
      };
      createdAt: string;
      updatedAt: string;
    }>('/prices', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async updatePrice(
    externalId: string,
    data: {
      solutionId: string;
      price: number;
    },
  ): Promise<{
    externalId: string;
    priceCategoryId: string;
    solutionId: string;
    price: number;
    solution: {
      externalId: string;
      name: string;
      alias: string;
    };
    priceCategory?: {
      externalId: string;
      solutionCategoryId: string;
      priceMin: number;
      priceMax: number;
      solutionCategory: {
        externalId: string;
        name: string;
      };
      createdAt: string;
      updatedAt: string;
    };
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      priceCategoryId: string;
      solutionId: string;
      price: number;
      solution: {
        externalId: string;
        name: string;
        alias: string;
      };
      priceCategory?: {
        externalId: string;
        solutionCategoryId: string;
        priceMin: number;
        priceMax: number;
        solutionCategory: {
          externalId: string;
          name: string;
        };
        createdAt: string;
        updatedAt: string;
      };
      createdAt: string;
      updatedAt: string;
    }>(`/prices/${externalId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async getSolutions(): Promise<{
    items: Array<{
      externalId: string;
      categoryId: string;
      category: string;
      vehicleTypeId: string;
      vehicleType: string;
      name: string;
      alias: string;
      level: number;
      isFreeWhenStageSelected: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      items: Array<{
        externalId: string;
        categoryId: string;
        category: string;
        vehicleTypeId: string;
        vehicleType: string;
        name: string;
        alias: string;
        level: number;
        isFreeWhenStageSelected: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('/solutions', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async deleteSolution(externalId: string): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/solutions/${externalId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateSolution(
    externalId: string,
    data: {
      categoryId: string;
      vehicleTypeId: string;
      name: string;
      alias: string;
      level: number;
      isFreeWhenStageSelected: boolean;
    },
  ): Promise<{
    externalId: string;
    categoryId: string;
    category: string;
    vehicleTypeId: string;
    vehicleType: string;
    name: string;
    alias: string;
    level: number;
    isFreeWhenStageSelected: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      categoryId: string;
      category: string;
      vehicleTypeId: string;
      vehicleType: string;
      name: string;
      alias: string;
      level: number;
      isFreeWhenStageSelected: boolean;
      createdAt: string;
      updatedAt: string;
    }>(`/solutions/${externalId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async getSolutionGroupPrices(externalId: string): Promise<
    Array<{
      groupId: string;
      groupName: string;
      creditAmount: number;
    }>
  > {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<
      Array<{
        groupId: string;
        groupName: string;
        creditAmount: number;
      }>
    >(`/solutions/${externalId}/group-prices`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateSolutionGroupPrice(
    externalId: string,
    groupId: string,
    data: { creditAmount: number },
  ): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(
      `/solutions/${externalId}/group-prices/${groupId}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      },
    );
  }

  async createSolution(data: {
    categoryId: string;
    vehicleTypeId: string;
    name: string;
    alias: string;
    level: number;
    isFreeWhenStageSelected: boolean;
  }): Promise<{
    externalId: string;
    categoryId: string;
    category: string;
    vehicleTypeId: string;
    vehicleType: string;
    name: string;
    alias: string;
    level: number;
    isFreeWhenStageSelected: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      externalId: string;
      categoryId: string;
      category: string;
      vehicleTypeId: string;
      vehicleType: string;
      name: string;
      alias: string;
      level: number;
      isFreeWhenStageSelected: boolean;
      createdAt: string;
      updatedAt: string;
    }>('/solutions', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async getPricingRules(
    vehicleTypeId?: string,
    customerGroupId?: string,
  ): Promise<{
    items: Array<{
      externalId: string;
      vehicleTypeId: string;
      vehicleType: {
        externalId: string;
        name: string;
      };
      customerGroupId: string;
      customerGroup: {
        externalId: string;
        name: string;
      };
      alias: string;
      creditAmount: number;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const queryParams = new URLSearchParams();
    if (vehicleTypeId) {
      queryParams.append('vehicleTypeId', vehicleTypeId);
    }
    if (customerGroupId) {
      queryParams.append('customerGroupId', customerGroupId);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/pricing-rules?${queryString}`
      : '/pricing-rules';

    return await this.request<{
      items: Array<{
        externalId: string;
        vehicleTypeId: string;
        vehicleType: {
          externalId: string;
          name: string;
        };
        customerGroupId: string;
        customerGroup: {
          externalId: string;
          name: string;
        };
        alias: string;
        creditAmount: number;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updatePricingRule(
    externalId: string,
    data: { creditAmount: number },
  ): Promise<void> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    await this.request<void>(`/pricing-rules/${externalId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async calculatePricing(data: {
    solutions: string[];
    vehicleTypeId: string;
  }): Promise<{
    creditAmount: number;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      creditAmount: number;
    }>('/pricing/calculate', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async getDashboard(): Promise<{
    balanceTopUpsByDay?: Array<{ date: string; value: number }>;
    completedFilesByDay?: Array<{ date: string; value: number }>;
    earnedCreditsByDay?: Array<{ date: string; value: number }>;
    filesInProgress?: number;
    activeCustomers?: number;
    totalCompletedFiles?: number;
    processedTickets?: number;
    openTickets?: number;
    eurTopUps7Days?: number;
    eurTopUps30Days?: number;
    eurTopUps365Days?: number;
    spentCreditsByDay?: Array<{ date: string; value: number }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return this.request<{
      balanceTopUpsByDay?: Array<{ date: string; value: number }>;
      completedFilesByDay?: Array<{ date: string; value: number }>;
      earnedCreditsByDay?: Array<{ date: string; value: number }>;
      filesInProgress?: number;
      activeCustomers?: number;
      totalCompletedFiles?: number;
      processedTickets?: number;
      openTickets?: number;
      eurTopUps7Days?: number;
      eurTopUps30Days?: number;
    eurTopUps365Days?: number;
      spentCreditsByDay?: Array<{ date: string; value: number }>;
    }>('/dashboard', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getRole(externalId: string): Promise<{
    externalId: string;
    name: string;
    permissions: Array<{
      externalId: string;
      action: string;
      resource: {
        externalId: string;
        name: string;
      };
    }>;
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return this.request<{
      externalId: string;
      name: string;
      permissions: Array<{
        externalId: string;
        action: string;
        resource: {
          externalId: string;
          name: string;
        };
      }>;
    }>(`/acl/roles/${externalId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateLanguage(language: string): Promise<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    isActive: boolean;
    userType: string;
    language: string;
    roleId: string;
    customer?: {
      id: string;
      companyName: string;
      vatNumber: string;
      evcNumber: string;
      city: string;
      address: string;
      postCode: string;
      countryId: string;
      autotunerEmail?: string;
      magicmotorsportsEmail?: string;
    };
  }> {
    const tokenRaw = localStorage.getItem('auth_token');
    const token = tokenRaw ? JSON.parse(tokenRaw) : null;

    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    return await this.request<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      isActive: boolean;
      userType: string;
      language: string;
      roleId: string;
      customer?: {
        id: string;
        companyName: string;
        vatNumber: string;
        evcNumber: string;
        city: string;
        address: string;
        postCode: string;
        countryId: string;
      };
    }>('/auth/language', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ language }),
    });
  }
}

export const apiService = new ApiService();
